import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import gsap from 'gsap';
import { animDuration } from 'src/lib/utils';
import TeamCoffeeCup from './TeamCoffeeCup';

export default class TeamIntroHeader extends View {
  private awning!: ImageScaleView;
  private branchLeft!: ImageScaleView;
  private branchRight!: ImageScaleView;
  private mascot1!: ImageScaleView;
  private mascot2!: ImageScaleView;
  private mascot3!: ImageScaleView;
  private mascot4!: ImageScaleView;
  private cup!: TeamCoffeeCup;
  private title!: ImageScaleView;

  constructor(opts: { superview: View; x?: number; y?: number }) {
    super({ ...opts, centerOnOrigin: true });
    this.addBackground();
    this.addMascots();
    this.cup = new TeamCoffeeCup({ superview: this, y: -67 });
    this.addTitle();
  }

  private addBackground() {
    this.awning = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_awning.png',
      width: 656,
      height: 97,
      offsetX: -656 * 0.5,
      scaleY: 0,
    });

    this.branchLeft = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_branch.png',
      width: 256,
      height: 228,
      anchorX: 256,
      anchorY: 126,
      offsetX: -256,
      offsetY: -126,
      x: -72,
      scaleX: 0,
      scaleY: 0,
    });

    this.branchRight = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_branch.png',
      width: 256,
      height: 228,
      anchorX: 256,
      anchorY: 135,
      offsetX: -256,
      offsetY: -135,
      x: 42,
      scaleX: 0,
      scaleY: 0,
    });
  }

  private addMascots() {
    this.mascot1 = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_mascot_1.png',
      width: 113,
      height: 134,
      anchorX: 113 * 0.5,
      anchorY: 134,
      x: -215,
      y: -145,
      scale: 0,
    });

    this.mascot2 = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_mascot_2.png',
      width: 95,
      height: 94,
      anchorX: 95 * 0.5,
      anchorY: 94,
      x: -145,
      y: -115,
      scale: 0,
    });

    this.mascot4 = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_mascot_4.png',
      width: 116,
      height: 119,
      anchorX: 116 * 0.5,
      anchorY: 119,
      x: 112,
      y: -155,
      scale: 0,
    });

    this.mascot3 = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/team/team_mascot_3.png',
      width: 77,
      height: 74,
      anchorX: 77 * 0.5,
      anchorY: 74,
      x: 78,
      y: -95,
      scale: 0,
    });
  }

  private addTitle() {
    const locale = StateObserver.getLocale();
    this.title = new ImageScaleView({
      superview: this,
      image: `assets/ui/match2/team/team_title_${locale}.png`,
      width: locale === 'en' ? 495 : 462,
      height: locale === 'en' ? 131 : 103,
      centerOnOrigin: true,
      y: -5,
      scale: 0,
    });
  }

  public async show() {
    let delay = animDuration * 2 * 0.001;

    // Awning
    gsap.to(this.awning.style, {
      duration: 0.6,
      scaleY: 1,
      ease: 'back.out(2)',
      delay,
    });

    delay += 0.2;

    // Title
    gsap.to(this.title.style, {
      duration: 1.5,
      scale: 1,
      ease: 'elastic.out(1, 0.4)',
      delay,
    });

    // Branches
    gsap.to(this.branchLeft.style, {
      duration: 0.4,
      scaleX: 1,
      scaleY: 1,
      ease: 'back.out(1.5)',
      delay,
      onComplete: () => {
        this.cup.show();
      },
    });
    gsap.to(this.branchRight.style, {
      duration: 0.4,
      scaleX: -1,
      scaleY: 1,
      ease: 'back.out(1.5)',
      delay,
    });
    gsap.to(this.branchLeft.style, {
      duration: 1.5,
      r: -0.06,
      ease: 'quad.inOut',
      repeat: -1,
      yoyo: true,
    });
    gsap.to(this.branchRight.style, {
      duration: 1.8,
      r: -0.06,
      repeat: -1,
      ease: 'quad.inOut',
      yoyo: true,
      delay: 0.3,
    });

    // Mascots
    delay += 0.4;
    gsap.to(this.mascot2.style, {
      duration: 0.4,
      scale: 1,
      ease: 'back.out',
      delay,
      onStart: () => {
        gsap.to(this.mascot2.style, {
          duration: 1,
          offsetY: -5,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        });
      },
    });

    delay += 0.15;
    gsap.to(this.mascot3.style, {
      duration: 0.4,
      scale: 1,
      ease: 'back.out',
      delay,
      onStart: () => {
        gsap.to(this.mascot3.style, {
          duration: 1,
          offsetY: -5,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        });
      },
    });
    delay += 0.15;
    gsap.to(this.mascot1.style, {
      duration: 0.4,
      scale: 1,
      ease: 'back.out',
      delay,
      onStart: () => {
        gsap.to(this.mascot1.style, {
          duration: 1,
          offsetY: -5,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        });
      },
    });
    delay += 0.15;
    await gsap.to(this.mascot4.style, {
      duration: 0.4,
      scale: 1,
      ease: 'back.out',
      delay,
      onStart: () => {
        gsap.to(this.mascot4.style, {
          duration: 1,
          offsetY: -5,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        });
      },
    });
  }

  public killAnimations() {
    gsap.killTweensOf(this.awning.style);
    gsap.killTweensOf(this.title.style);
    gsap.killTweensOf(this.branchLeft.style);
    gsap.killTweensOf(this.branchRight.style);
    gsap.killTweensOf(this.mascot1.style);
    gsap.killTweensOf(this.mascot2.style);
    gsap.killTweensOf(this.mascot3.style);
    gsap.killTweensOf(this.mascot4.style);
  }
}
